import { changeTheme } from '@snitch/ui/lib/motion'
import { useSyncExternalStore } from 'react'
import { telegramIsDark } from './telegram.ts'

export type ThemePreference = 'system' | 'light' | 'dark'

const KEY = 'snitch-theme'
const media = () => window.matchMedia('(prefers-color-scheme: dark)')
const listeners = new Set<() => void>()
let current: ThemePreference = 'system'
let stopFollowingSystem: () => void = () => {}

export function readThemePreference(): ThemePreference {
  try {
    const v = localStorage.getItem(KEY)
    return v === 'light' || v === 'dark' ? v : 'system'
  } catch {
    return 'system'
  }
}

/** "system" means Telegram's theme inside the Mini App and the OS theme in a browser. */
export function resolveDark(preference: ThemePreference): boolean {
  if (preference !== 'system') return preference === 'dark'
  return telegramIsDark() ?? media().matches
}

/** Puts the theme on <html data-theme>; with "system" it keeps following OS changes. */
export function applyTheme(preference: ThemePreference): void {
  current = preference
  stopFollowingSystem()
  const root = document.documentElement
  const set = () => {
    root.dataset.theme = resolveDark(preference) ? 'dark' : 'light'
  }
  set()
  if (preference === 'system') {
    const mq = media()
    mq.addEventListener('change', set)
    stopFollowingSystem = () => mq.removeEventListener('change', set)
  } else {
    stopFollowingSystem = () => {}
  }
  for (const l of listeners) l()
}

/** The profile's "Тема" sheet. Remembered per device; storage may be unavailable (private mode). */
export function setThemePreference(preference: ThemePreference): void {
  try {
    if (preference === 'system') localStorage.removeItem(KEY)
    else localStorage.setItem(KEY, preference)
  } catch {
    // Still applies for this visit.
  }
  changeTheme(() => applyTheme(preference))
}

/** The header button: flips between light and dark. */
export function toggleTheme(): void {
  setThemePreference(resolveDark(current) ? 'light' : 'dark')
}

export function useThemePreference(): ThemePreference {
  return useSyncExternalStore(
    (onChange) => {
      listeners.add(onChange)
      return () => listeners.delete(onChange)
    },
    () => current,
  )
}
